import { data, Link, useLoaderData } from 'react-router'
import { BusinessCard } from '#app/components/business-card.tsx'
import { GlobalSearch } from '#app/components/global-search.tsx'
import { RentalCard } from '#app/components/rental-card.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/index.ts'
import { type IconName } from '@/icon-name'

export async function loader(_args: Route.LoaderArgs) {
	const [categories, recentRentals, featuredBusinesses, homeSeo] =
		await Promise.all([
			// Get categories with rental listing counts
			prisma.category.findMany({
				where: {
					rentalListings: { some: { status: 'published' } },
				},
				select: {
					id: true,
					name: true,
					slug: true,
					icon: true,
					_count: {
						select: {
							rentalListings: {
								where: { status: 'published' },
							},
						},
					},
				},
				orderBy: {
					name: 'asc',
				},
			}),

			// Get 9 most recent published rental listings
			prisma.rentalListing.findMany({
				where: {
					status: 'published',
				},
				select: {
					id: true,
					slug: true,
					title: true,
					description: true,
					hourlyRateCents: true,
					dailyRateCents: true,
					weeklyRateCents: true,
					monthlyRateCents: true,

					featuredMedia: {
						select: {
							url: true,
							altText: true,
						},
					},
					categories: {
						select: {
							id: true,
							name: true,
							slug: true,
						},
					},
					subcategories: {
						select: {
							id: true,
							name: true,
							slug: true,
						},
					},
					cities: {
						select: {
							id: true,
							name: true,
							slug: true,
						},
					},
					ownerBusiness: {
						select: {
							id: true,
							name: true,
							slug: true,
						},
					},
					ownerUser: {
						select: {
							id: true,
							name: true,
						},
					},
				},
				orderBy: {
					createdAt: 'desc',
				},
				take: 9,
			}),

			// Get featured businesses
			prisma.business.findMany({
				where: {
					status: 'published',
					isFeatured: true,
				},
				select: {
					id: true,
					name: true,
					isFeatured: true,
					slug: true,
					description: true,
					aiDescription: true,
					phone: true,
					website: true,
					address: true,
					cities: {
						select: {
							id: true,
							name: true,
							slug: true,
						},
					},
					categories: {
						select: {
							id: true,
							name: true,
							slug: true,
						},
					},
					subcategories: {
						select: {
							id: true,
							name: true,
							slug: true,
						},
					},
				},
				orderBy: [{ createdAt: 'desc' }],
				take: 6,
			}),

			// Get SEO settings
			prisma.sEOSettings.findUnique({
				where: { page: 'HOME' },
				select: {
					title: true,
					description: true,
					canonicalUrl: true,
					focusKeyword: true,
					seoKeywords: true,
					noIndex: true,
					ogTitle: true,
					ogDescription: true,
					ogImage: true,
					twitterTitle: true,
					twitterDescription: true,
					structuredData: true,
				},
			}),
		])

	return data({
		categories,
		recentRentals,
		featuredBusinesses,
		homeSeo,
	})
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb() {
		return { name: 'Home', url: '/' }
	},
	seo({
		loaderData,
		origin,
		canonical,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
		canonical: string
	}): SeoProps {
		const seo = loaderData.homeSeo

		// Build keywords from focus keyword and SEO keywords
		const keywords = [seo?.focusKeyword, ...(seo?.seoKeywords || [])].filter(
			Boolean,
		)

		// Default fallback values
		const defaultTitle =
			'Rent DFW - Equipment & Party Rentals in Dallas-Fort Worth'
		const defaultDescription =
			'Browse equipment rentals, party supplies, and more in the Dallas-Fort Worth area. Find what you need from local businesses.'

		// Build structured data (include custom structured data if available)
		const structuredData: Array<object | string> = []

		if (loaderData.recentRentals.length > 0) {
			structuredData.push(
				buildItemListLd({
					name: 'Recent Rental Listings',
					items: loaderData.recentRentals.map((r) => ({
						name: r.title,
						url: `${origin}/rentals/${r.slug}`,
					})),
				}),
			)
		}

		if (loaderData.featuredBusinesses.length > 0) {
			structuredData.push(
				buildItemListLd({
					name: 'Featured Businesses',
					items: loaderData.featuredBusinesses.map((b) => ({
						name: b.name,
						url: `${origin}/businesses/${b.slug}`,
					})),
				}),
			)
		}

		// Append custom structured data from DB if present
		if (seo?.structuredData) {
			const customData = seo.structuredData
			if (Array.isArray(customData)) {
				structuredData.push(...(customData as Array<object | string>))
			} else if (customData !== null) {
				structuredData.push(customData as object | string)
			}
		}

		return {
			title: seo?.title || defaultTitle,
			description: seo?.description || defaultDescription,
			canonical: seo?.canonicalUrl || canonical,
			keywords: keywords.length > 0 ? keywords : undefined,
			robots: seo?.noIndex ? 'noindex, nofollow' : undefined,
			openGraph: {
				title: seo?.ogTitle || defaultTitle,
				description: seo?.ogDescription || defaultDescription,
				...(seo?.ogImage && { image: seo.ogImage }),
				type: 'website',
				url: seo?.canonicalUrl || canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: seo?.twitterTitle || defaultTitle,
				description: seo?.twitterDescription || defaultDescription,
			},
			structuredData: structuredData.length > 0 ? structuredData : undefined,
		}
	},
}

export default function Index() {
	const { categories, recentRentals, featuredBusinesses } =
		useLoaderData<typeof loader>()

	return (
		<>
			{/* Hero Section with CTA and Search */}
			<div className="bg-black/70 bg-[url('/img/dallas-fort-worth-downtown.jpg')] bg-cover bg-center bg-no-repeat bg-blend-multiply">
				<div className="container mx-auto px-4 py-24">
					<div className="mx-auto max-w-4xl text-center text-white">
						<h1 className="text-4xl font-semibold text-balance md:text-6xl">
							Rent anything in{' '}
							<span className="font-black text-red-500">Dallas-Fort Worth</span>
						</h1>
						<p className="mt-6 text-xl text-balance">
							From heavy equipment for your job site to party supplies for your
							event. Peer-to-peer and local businesses, all in one place.
						</p>

						{/* Search Feature */}
						<div className="mx-auto mt-10 max-w-2xl">
							<GlobalSearch />
						</div>

						{/* CTA Buttons */}
						<div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
							<Button asChild size="lg">
								<Link to="/rentals">
									<Icon name="magnifying-glass" />
									Browse All Rentals
								</Link>
							</Button>
						</div>
					</div>
				</div>
			</div>

			{/* Browse by Category Section */}
			<div className="py-16">
				<div className="container mx-auto px-4">
					<div className="mb-10">
						<h2 className="text-xl font-bold">Browse by Rental Category</h2>
					</div>

					{categories.length > 0 ? (
						<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
							{categories.map((category) => (
								<Link
									key={category.id}
									to={`/rentals/category/${category.slug}`}
									className="group"
								>
									<div className="bg-card group-hover:border-primary flex h-full flex-col items-center justify-between gap-2 rounded-lg border p-6 transition-all duration-300 group-hover:shadow-md">
										<div className="group-hover:bg-primary flex size-12 items-center justify-center rounded-full bg-white transition-all duration-300 group-hover:text-white dark:bg-black">
											<Icon
												name={(category.icon || 'scan-search') as IconName}
												className="size-7 opacity-60 transition-opacity duration-300 group-hover:opacity-100"
											/>
										</div>
										<h3 className="text-sm font-medium">{category.name}</h3>
									</div>
								</Link>
							))}
						</div>
					) : (
						<div className="rounded-lg py-16 text-center">
							<Icon
								name="file-text"
								className="text-muted-foreground mx-auto mb-4 size-12"
							/>
							<p className="text-muted-foreground">
								No categories available yet.
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Free Tools Section */}
			<div className="bg-muted/50 py-16">
				<div className="container mx-auto px-4">
					<div className="mb-8">
						<h2 className="text-xl font-bold">Free Rental Tools</h2>
						<p className="text-muted-foreground mt-1">
							Compare prices and find what you need across DFW
						</p>
					</div>
					<div className="grid gap-4 md:grid-cols-2">
						<Link to="/tools/dumpster-size-calculator" className="group">
							<div className="bg-card group-hover:border-primary flex items-start gap-4 rounded-lg border p-5 transition-all group-hover:shadow-md">
								<div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
									<Icon name="truck" className="text-primary size-5" />
								</div>
								<div>
									<h3 className="font-semibold group-hover:text-primary transition-colors">
										Dumpster Size & Cost Calculator
									</h3>
									<p className="text-muted-foreground mt-1 text-sm">
										Find the right dumpster size and compare prices from DFW
										vendors.
									</p>
								</div>
							</div>
						</Link>
						<Link to="/tools/equipment-cost-estimator" className="group">
							<div className="bg-card group-hover:border-primary flex items-start gap-4 rounded-lg border p-5 transition-all group-hover:shadow-md">
								<div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
									<Icon name="drill" className="text-primary size-5" />
								</div>
								<div>
									<h3 className="font-semibold group-hover:text-primary transition-colors">
										Equipment & Tool Rental Cost Estimator
									</h3>
									<p className="text-muted-foreground mt-1 text-sm">
										Compare daily, weekly, and monthly rates for 40+ equipment
										types across DFW.
									</p>
								</div>
							</div>
						</Link>
					</div>
				</div>
			</div>

			{/* Recent Rentals Section */}
			<div className="py-16">
				<div className="container mx-auto px-4">
					<div className="mb-10 flex items-end justify-between">
						<div>
							<h2 className="text-3xl font-bold">Recent Rentals</h2>
							<p className="text-muted-foreground mt-2">
								Latest Dallas-Fort Worth rentals available
							</p>
						</div>
						<Button asChild variant="ghost">
							<Link to="/rentals">
								View All
								<Icon name="chevron-right" />
							</Link>
						</Button>
					</div>

					{recentRentals.length > 0 ? (
						<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
							{recentRentals.map((rental) => (
								<RentalCard key={rental.id} rental={rental} />
							))}
						</div>
					) : (
						<div className="bg-muted rounded-lg py-16 text-center">
							<Icon
								name="file-text"
								className="text-muted-foreground mx-auto mb-4 size-12"
							/>
							<h3 className="mb-2 text-xl font-semibold">
								No Rentals Available
							</h3>
							<p className="text-muted-foreground">
								Check back soon for rental listings.
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Featured Businesses Section */}
			<div className="py-16">
				<div className="container mx-auto px-4">
					<div className="flex flex-col items-start gap-6 lg:flex-row">
						{/* Text Side */}
						<div className="w-full lg:w-1/3">
							<span className="mb-4 inline-block rounded-full bg-blue-50 px-3 py-1 text-sm font-bold text-blue-600">
								Trusted Partners
							</span>
							<h2 className="mb-4 text-3xl font-bold text-slate-900 md:text-4xl dark:text-slate-50">
								Support Local DFW Rental Businesses
							</h2>
							<p className="mb-6 text-lg leading-relaxed text-gray-600 dark:text-gray-400">
								Find verified professional rental companies right here in the
								Metroplex. From large-scale event planners to industrial
								equipment suppliers.
							</p>
							<ul className="mb-8 space-y-3">
								<li className="flex items-center text-gray-700 dark:text-gray-300">
									<Icon
										name="check-circled"
										className="mr-2 size-5 text-green-500"
									/>
									Verified Insurance & Licenses
								</li>
								<li className="flex items-center text-gray-700 dark:text-gray-300">
									<Icon
										name="check-circled"
										className="mr-2 size-5 text-green-500"
									/>
									Professional Maintenance
								</li>
								<li className="flex items-center text-gray-700 dark:text-gray-300">
									<Icon
										name="check-circled"
										className="mr-2 size-5 text-green-500"
									/>
									Delivery & Assistance
								</li>
								<li className="flex items-center text-gray-700 dark:text-gray-300">
									<Icon
										name="check-circled"
										className="mr-2 size-5 text-green-500"
									/>
									Reliable Availability
								</li>
							</ul>
							<Button asChild variant="ghost" className="group px-0">
								<Link to="/businesses" className="font-bold">
									Browse Business Directory
									<Icon
										name="arrow-right"
										className="ml-2 transition-transform group-hover:translate-x-1"
									/>
								</Link>
							</Button>
						</div>

						{/* Cards Side - Grid, no horizontal scroll */}
						<div className="w-full lg:w-2/3">
							{featuredBusinesses.length > 0 ? (
								<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
									{featuredBusinesses.slice(0, 5).map((business) => (
										<div key={business.id}>
											<BusinessCard business={business} />
										</div>
									))}

									{/* CTA Card */}
									<Link
										to="/submit-business"
										className="flex items-center justify-center rounded-xl bg-blue-600 p-2 text-center text-white transition-colors hover:bg-blue-700"
									>
										<div>
											<h4 className="mb-2 text-xl font-bold">
												Own a rental business?
											</h4>
											<p className="mb-4 text-sm text-blue-200">
												List your inventory on RentDFW today.
											</p>
											<span className="font-semibold underline">
												Submit Your Business
											</span>
										</div>
									</Link>
								</div>
							) : (
								<div className="bg-muted rounded-lg py-16 text-center">
									<Icon
										name="file-text"
										className="text-muted-foreground mx-auto mb-4 size-12"
									/>
									<h3 className="mb-2 text-xl font-semibold">
										No Businesses Available
									</h3>
									<p className="text-muted-foreground">
										Check back soon for business listings.
									</p>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	)
}
